function getPoWishlistStoredContext() {
    return window.__poWishlistTrackingContext || null;
}

function clearPoWishlistStoredContext() {
    window.__poWishlistTrackingContext = null;
}

function parsePoWishlistProductData(rawProductData = null) {
    if (!rawProductData) return null;

    if (typeof rawProductData === 'string') {
        try {
            return JSON.parse(rawProductData);
        } catch (error) {
            return null;
        }
    }

    return typeof rawProductData === 'object' ? rawProductData : null;
}

function normalizePoWishlistTealiumCategory(value) {
    const normalizedValue = String(value || '')
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase();

    const categoryMap = {
        accessories: 'ACCESSORIES',
        accessory: 'ACCESSORIES',
        optics: 'OPTICS',
        optical: 'OPTICS',
        eyeglasses: 'OPTICS',
        eyeglass: 'OPTICS',
        sun: 'SUN',
        sunglasses: 'SUN',
        sunglass: 'SUN',
        afa: 'AFA',
        eyewear: 'EYEWEAR',
        electronics: 'Electronics',
        electronic: 'Electronics',
        giftcard: 'GIFTCARD',
        'gift card': 'GIFTCARD',
        'gift cards': 'GIFTCARD',
        'contact lens': 'CONTACT LENSES',
        'contact lenses': 'CONTACT LENSES',
        vas: 'VAS',
        service: 'VAS',
        services: 'VAS',
        'value added service': 'VAS',
        'value added services': 'VAS',
    };

    if (categoryMap[normalizedValue]) {
        return categoryMap[normalizedValue];
    }

    if (normalizedValue.includes('contact lens')) return 'CONTACT LENSES';
    if (normalizedValue.includes('gift card')) return 'GIFTCARD';
    if (normalizedValue.includes('electronic')) return 'Electronics';
    if (normalizedValue.includes('accessor')) return 'ACCESSORIES';
    if (normalizedValue.includes('optic') || normalizedValue.includes('eyeglass')) return 'OPTICS';
    if (normalizedValue.includes('sun')) return 'SUN';
    if (normalizedValue.includes('afa')) return 'AFA';
    if (normalizedValue.includes('vas')) return 'VAS';

    return 'EYEWEAR';
}

function resolvePoWishlistTealiumContext(rawContext = null) {
    const context = rawContext || getPoWishlistStoredContext() || {};
    if (typeof window.poResolveTealiumProductEntry === 'function') {
        const resolvedEntry = window.poResolveTealiumProductEntry({
            productHandle: context.productHandle,
            variantSku: context.variantSku,
            productId: context.productId,
            variantId: context.variantId,
            productData: context.productData
        });
        const fallbackProductData = parsePoWishlistProductData(context.productData);
        const resolvedProductHandle =
            context.productHandle ||
            resolvedEntry?.data?.Handle ||
            resolvedEntry?.data?.handle ||
            fallbackProductData?.Handle ||
            fallbackProductData?.handle ||
            '';

        if (resolvedEntry?.key) {
            return {
                productHandle: resolvedProductHandle,
                productKey: resolvedEntry.key,
                productData: resolvedEntry.data || fallbackProductData || null
            };
        }
    }

    const tealiumProducts = window.tealium?.products || {};
    const currentProductId = window.tealium?.currentProductId || '';
    const currentProduct = currentProductId ? tealiumProducts[currentProductId] : null;
    const fallbackProductData = parsePoWishlistProductData(context.productData);
    const firstProductEntry = Object.entries(tealiumProducts).find(([, product]) => (
        product && typeof product === 'object'
    )) || null;

    const productHandle =
        context.productHandle ||
        fallbackProductData?.Handle ||
        fallbackProductData?.handle ||
        currentProduct?.Handle ||
        currentProduct?.handle ||
        '';

    const directKey = context.variantSku || context.variantId || context.productId || currentProductId || '';
    let matchedEntry = null;

    if (directKey && tealiumProducts[directKey]) {
        matchedEntry = [directKey, tealiumProducts[directKey]];
    } else if (currentProductId && currentProduct) {
        matchedEntry = [currentProductId, currentProduct];
    } else if (productHandle) {
        matchedEntry = Object.entries(tealiumProducts).find(([, product]) => {
            const handle = String(product?.Handle || product?.handle || '').trim().toLowerCase();
            return handle && handle === String(productHandle).trim().toLowerCase();
        }) || null;
    } else if (firstProductEntry) {
        matchedEntry = firstProductEntry;
    }

    if (!matchedEntry && firstProductEntry) {
        matchedEntry = firstProductEntry;
    }

    return {
        productHandle,
        productKey: matchedEntry?.[0] || directKey || '',
        productData: matchedEntry?.[1] || fallbackProductData || currentProduct || firstProductEntry?.[1] || null
    };
}

function buildPoWishlistTealiumEvent(rawContext = null, eventKey = 'add') {
    const context = resolvePoWishlistTealiumContext(rawContext);
    if (!context.productKey) {
        return null;
    }

    const productPrice = context.productHandle
        ? window.opTealiumPrices?.find((item) => item.productHandle === context.productHandle)
        : null;

    const formatCentsPrice = (value) => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? (numericValue / 100).toFixed(2) : '';
    };

    const productPayload = context.productData && typeof context.productData === 'object'
        ? { ...context.productData }
        : {};

    if (productPrice?.price !== undefined) {
        productPayload.Price = formatCentsPrice(productPrice.price);
        productPayload.PriceNet = formatCentsPrice(productPrice.price);
    } else if (context.productData?.Price) {
        productPayload.Price = String(context.productData.Price);
        if (context.productData?.PriceNet) {
            productPayload.PriceNet = String(context.productData.PriceNet);
        }
    }

    if (productPrice?.priceFull !== undefined) {
        productPayload.PriceFull = formatCentsPrice(productPrice.priceFull);
        productPayload.PriceFullNet = formatCentsPrice(productPrice.priceFull);
    } else if (context.productData?.PriceFull) {
        productPayload.PriceFull = String(context.productData.PriceFull);
        if (context.productData?.PriceFullNet) {
            productPayload.PriceFullNet = String(context.productData.PriceFullNet);
        }
    }

    if (!productPayload.LensType) {
        productPayload.LensType = 'PLANO';
    }

    if (productPayload.Category) {
        productPayload.Category = normalizePoWishlistTealiumCategory(productPayload.Category);
    }

    const tealiumEventFlag = eventKey === 'remove'
        ? 'Events_ProdFavRem'
        : 'Events_ProdFavAdd';

    if (eventKey === 'add') {
        delete productPayload.PriceNet;
        delete productPayload.PriceFullNet;
    }

    return {
        id: 'Event',
        [tealiumEventFlag]: '1',
        Products: {
            [context.productKey]: productPayload
        }
    };
}

function sendPoWishlistTealiumTracking(rawContext = null, eventKey = 'add') {
    const tealiumEvent = buildPoWishlistTealiumEvent(rawContext, eventKey);
    if (!tealiumEvent) {
        return false;
    }

    if (typeof window.sendTealiumEvent === 'function') {
        window.sendTealiumEvent(tealiumEvent);
        return true;
    }

    if (typeof window.tealium_data2track !== 'undefined' && typeof window.tealium_data2track.push === 'function') {
        window.tealium_data2track.push(tealiumEvent);
        return true;
    }

    return false;
}

window.sendPoWishlistTealiumTracking = sendPoWishlistTealiumTracking;

// Listen to this event to ensure the WL is loaded
function SWCallbackWishlistButtonLoaded() {
    publish(PUB_SUB_EVENTS.wishlistLoaded)
}

// PLP
function SWCallbackExpressWishlistCollectionItemAdded() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext);
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.wishlistCollectionItemAdded);
}

function SWCallbackExpressWishlistCollectionItemRemoved() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext, 'remove');
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.wishlistCollectionItemRemoved);
}

function SWCallbackCustomerWishlistCollectionItemAdded() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext);
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.customerWishlistCollectionItemAdded);
}

function SWCallbackCustomerWishlistCollectionItemRemoved() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext, 'remove');
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.customerWishlistCollectionItemRemoved);
}

// PDP
function SWCallbackExpressWishlistProductItemAdded() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext);
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.wishlistProductItemAdded);
}

function SWCallbackExpressWishlistProductItemRemoved() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext, 'remove');
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.wishlistProductItemRemoved);
}

function SWCallbackCustomerWishlistProductItemAdded() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext);
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.customerWishlistProductItemAdded);
}

function SWCallbackCustomerWishlistProductItemRemoved() {
    const trackingContext = getPoWishlistStoredContext();
    if (trackingContext) {
        sendPoWishlistTealiumTracking(trackingContext, 'remove');
        clearPoWishlistStoredContext();
    }
    publish(PUB_SUB_EVENTS.customerWishlistProductItemRemoved);
}
